<template>
	<div>
		<app-app-header v-model="indexStore.isHeaderVisible"
						:title="userStore.user.display_name"
						:breadcrumbs="[{text: userStore.user.presentation_type_name, to: $web('users.index')}]"
						:image="{path: userStore.user.avatar_thumbnail,round: true}"
						:color="userStore.user.color ?? $config.public.colors.primary"
						:to="$web('users.show', [userStore.user._key])">
			<client-only>
				<div v-if="userStore.authPermissions.is_initialized" class="flex items-center justify-end">
					<btn v-if="userStore.authPermissions.can_edit && userStore.authPermissions.is_user" fab :aria-label="$t('action.edit')" class="ml-12 sm:ml-16" :to="$web('my.profile.manage.index')" exact>
						<icon :icon="icons.edit"/>
					</btn>
					<follow-text-button v-else :model="userStore.user" :color="userStore.user.color" small class="w-88" @follow="() => updateFollowingState(true)"
										@unfollow="() => updateFollowingState(false)"/>

					<dropdown bottom nudge-right :menu-title="userStore.user.display_name" :actions="actions">
						<btn fab :aria-label="$t('label.actions')" class="ml-12 sm:ml-16">
							<icon :icon="icons.options"/>
						</btn>
					</dropdown>

				</div>
			</client-only>
		</app-app-header>
		<app-content>
			<page-content xs-full sm-full background :has-plain-footer="!$device.isNative">
				<container xs-full sm-full class="stretch-layout">
					<card :style="`border-top: 2px solid ${userStore.user.color ?? $config.public.colors.primary}`" xs-tile sm-tile>
						<card-text class="pt-20 md:pt-32 pb-12 md:pb-20">
							<div class="flex flex-column items-center md:flex-row justify-between">
								<div class="flex items-center flex-column md:flex-row">
									<div class="relative md:mr-32 mb-16 md:mb-0">
										<profile-avatar :src="userStore.user.avatar"
														:thumbnail-src="userStore.user.avatar_thumbnail"
														:editable="userStore.authPermissions.can_edit"
														:action="`/v1/athletes/${userStore.user.id}`"
														@updated="avatarUpdated" round/>
										<nuxt-link v-if="userStore.user.club && userStore.user.club.avatar_thumbnail" class="absolute right-0 bottom-0" :to="$web('clubs.show', userStore.user.club._key)">
											<avatar :model="userStore.user.club" :size="32"/>
										</nuxt-link>
									</div>

									<div class="mb-16 md:mb-0">
										<nuxt-link v-if="userStore.user.club" class="block text-center md:text-left mb-2 leading-normal" :to="$web('clubs.show', userStore.user.club._key)" :style="{color: userStore.user.color ?? $config.public.colors.primary}">{{ userStore.user.club.display_name }}</nuxt-link>

										<p class="block font-heading text-heading-color font-bold leading-normal text-2xl md:text-3xl mb-4 text-center md:text-left">
											{{ userStore.user.display_name }}</p>

										<sport-type-text :sport-types="userStore.user.sportTypes" :truncate="2" class="text-subtext-color text-center md:text-left justify-center md:justify-start leading-wide"/>

									</div>
								</div>

								<div class="w-full md:w-unset flex flex-column md:flex-row justify-center md:justify-end items-center">
									<nuxt-link class="mx-12 flex flex-column items-center justify-center" :to="$web('users.followers.index', userStore.user._key)">
										<p class="font-bold mb-4 text-text-color text-xl leading-none font-heading">{{ userStore.user.followers_count }}</p>
										<span class="text-subtext-color text-2xs leading-none">
											{{ $t('label.fans') }}
										</span>
									</nuxt-link>

									<div v-if="$device.isDesktop" class="flex mt-16 md:mt-0 md:ml-20 justify-center md:justify-end h-40 w-220">
										<div class="w-160">
											<client-only>
												<template v-if="userStore.authPermissions.is_initialized">
													<btn v-if="userStore.authPermissions.can_edit && userStore.authPermissions.is_user" :color="userStore.user.color ?? 'primary'" outlined
														 class="w-full"
														 :to="$web('my.profile.manage.index')">
														<icon :icon="icons.edit"/>
														<span>{{ $t('action.manage_profile') }}</span>
													</btn>
													<follow-text-button v-else :model="userStore.user" :color="userStore.user.color" class="w-full"
																		@follow="() => updateFollowingState(true)" @unfollow="() => updateFollowingState(false)"/>
												</template>
											</client-only>
										</div>

										<dropdown bottom nudge-right min-width="200" class="ml-8" :actions="actions">
											<btn outlined :color="userStore.user.color ?? 'primary'" :aria-label="$t('label.actions')">
												<icon :icon="icons.options"/>
											</btn>
										</dropdown>
									</div>
								</div>
							</div>
						</card-text>

						<header-toolbar id="tabs" sticky-mobile>
							<tabs class="h-full" grow>
								<tab v-for="(tabChildRoute, tabKey) in tabRoutes" replace :exact="tabChildRoute.exact"
									 :key="tabKey"
									 class="lg:px-24"
									 :color="user.color"
									 :to="tabChildRoute.to"
									 :text="tabChildRoute.title"/>
							</tabs>
						</header-toolbar>
					</card>

					<!-- To allow the devices to scroll to the tabs even though a page has no content, we need to set the min height for our mobile profile page tab focus scrolling logic. -->
					<!-- 56px = header; 42px = tabs bar; 48px = mobile bottom bar; 8px = spacing top -->
					<div id="content" class="stretch-layout" :style="{minHeight: $device.isDesktop ? null : 'calc(100vh - 56px - 42px - 48px - 8px)'}">
						<NuxtPage keep-alive/>
					</div>

				</container>
			</page-content>
		</app-content>
		<lazy-user-edit-modal v-if="userStore.authPermissions.can_edit && !userStore.authPermissions.is_user" v-model="showEditModal" :user-key="userStore.user._key" @success="userStore.updateStoreUser"/>
	</div>
</template>

<script setup lang="ts">
import {mdiAccount} from '@mdi/js';
import {editIcon, optionsIcon} from "@spoferan/nuxt-spoferan/icons";
import {useIndexStore} from "../../store";
import {useUserStore} from "../../store/user";
import {useGoogleSchema} from "../../composables/useGoogleSchema";
import {PrivacyType} from "@spoferan/spoferan-ts-core";

const route = useRoute();
const {$image, $date, $auth, $device, $web, $analytics} = useNuxtApp();
const indexStore = useIndexStore();
const userStore = useUserStore();
const {t} = useI18n();
const {getUserSeoDescription} = useGoogleSchema();

const {value: cacheBusterValue} = useCacheBuster('user_ts');

// Refresh permissions on user change as required when user gets authenticated on client side after SSR hydration
watch(() => $auth.token, () => {
	userStore.refreshAuthPermissions();
}, {
	// Run immediately when token is already set (CSR) or the token will not be set as the user is not logged in
	immediate: !!$auth.token || !$auth.loggedIn
});

const {data: user} = await useApiFetch(`/v1/views/users/${route.params.user}`, {
	guest: route.params.user !== 'my', // We might use the 'my' alias to access the authenticated user's profile when we know that a user is logged in but the slug is not known
	params: {
		ts: cacheBusterValue,
	}
});

// If a hashed '#' char specified as '%23' is given in the URL, we do not want it for comparison as this would cause infinite redirects
const plainUserParam = route.params.user.split('#')[0];
if (user && user._key !== plainUserParam) {
	await navigateTo({params: {user: user._key}}, {
		redirectCode: 308
	});
}

userStore.user = user;

const icons = {
	user: mdiAccount,
	edit: editIcon,
	options: optionsIcon,
};

const tabRoutes = [
	{to: $web('users.show.index', route.params.user), title: t('label.profile'), exact: true},
	{to: $web('users.show.feed', route.params.user), title: 'Feed'},
	userStore.user.blog_cache_buster ? {to: `/athletes/${route.params.user}/blog`, title: t('label.blog')} : null,
	userStore.user.privacy_events !== PrivacyType.Private ? {to: `/athletes/${route.params.user}/results`, title: t('label.results')} : null,
	userStore.user.privacy_events !== PrivacyType.Private ? {to: `/athletes/${route.params.user}/events`, title: t('label.competition_planner')} : null,
	userStore.user.privacy_clubs !== PrivacyType.Private && userStore.user.clubs_count ? {to: $web('users.show.memberships', route.params.user), title: t('label.clubs_teams')} : null
].filter(Boolean);

const showEditModal = ref(!!route.query.connect_type);

const actions = computed(() => {
	return [
		userStore.authPermissions.can_edit && !userStore.authPermissions.is_user ? {
			name: t('action.edit'),
			callback: () => showEditModal.value = true
		} : undefined,
		userStore.authPermissions.can_edit && userStore.authPermissions.is_user ? {
			name: t('action.edit'),
			to: $web('my.profile.manage.index')
		} : undefined,
		{
			name: t('action.share'),
			callback: () => {
				indexStore.share({
					id: `user-${userStore.user._key}`
				})
			}
		}
	].filter(Boolean);
});

function avatarUpdated(userWithNewAvatar) {
	userStore.updateStoreUser({
		avatar_thumbnail: userWithNewAvatar.avatar_thumbnail,
		avatar: userWithNewAvatar.avatar,
		color: userWithNewAvatar.color,
	})
}

function updateFollowingState(isFollowing) {
	userStore.user.followers_count = isFollowing ? (userStore.user.followers_count + 1) : (userStore.user.followers_count - 1);
}

useMeta({
	title: userStore.buildPageTitle(user.display_name),
	description: getUserSeoDescription(userStore.user),
	type: 'profile',
	meta: [{
		'property': 'og:profile:username',
		'content': userStore.user.display_name
	},    {
		'property': 'og:profile:first_name',
		'content': userStore.user.first_name
	},
		{
			'property': 'og:profile:last_name',
			'content': userStore.user.last_name
		},
		{
			'property': 'og:profile:gender',
			'content': userStore.user.gender === 'm' ? 'male' : 'female'
		}],
	image: userStore.user.avatar_thumbnail ? $image.format(userStore.user.avatar_thumbnail, {width: 1200, height: 630, fit: 'contain'}) : null,
	noIndex: !userStore.user.is_public // Hide page for search engines, if not visible for public
});

useSchemaOrg({
	'@type': 'Person',
	'name': userStore.user.display_name,
	'url': $web('users.show', userStore.user._key, true),
	'sameAs': [userStore.user.socialMedia.facebook_url, userStore.user.socialMedia.instagram_url, userStore.user.socialMedia.twitter_url, userStore.user.socialMedia.youtube_url].filter(url => !!url)
})


</script>

